/**
 * useArbitrationSocket Hook
 *
 * Custom hook for real-time arbitration chat functionality
 * Handles Socket.IO connection and message events
 *
 * Follows Single Responsibility Principle - only handles socket logic
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { IChatMessage } from '../services/arbitrationService';
import { SOCKET_URL } from '../config/env';

interface UseArbitrationSocketOptions {
  chatId: string | null;
  enabled?: boolean;
  onNewMessage?: (message: IChatMessage) => void;
  onTyping?: (data: { userId: string; userName: string }) => void;
  onStopTyping?: (data: { userId: string }) => void;
  onUserJoined?: (data: { userId: string; userName: string }) => void;
  onUserLeft?: (data: { userId: string; userName: string }) => void;
  onChatClosed?: () => void;
}

interface UseArbitrationSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  typingUsers: Map<string, string>;
  sendTyping: () => void;
  sendStopTyping: () => void;
  joinChat: () => void;
  leaveChat: () => void;
}

// آدرس سوکت از منبع واحد env (src/config/env.ts) import شده است.
export const useArbitrationSocket = ({
  chatId,
  enabled = true,
  onNewMessage,
  onTyping,
  onStopTyping,
  onUserJoined,
  onUserLeft,
  onChatClosed,
}: UseArbitrationSocketOptions): UseArbitrationSocketReturn => {
  const socketRef = useRef<Socket | null>(null);
  const effectiveChatIdRef = useRef<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());

  // Typing timeout refs
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-expire timers for RECEIVED typing indicators — اگر «توقف تایپ» نرسد،
  // نشانگر بعد از ۴ ثانیه خودکار پاک می‌شود (جلوگیری از «همیشه در حال تایپ»)
  const typingExpireTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Initialize socket connection
  useEffect(() => {
    if (!enabled || !chatId) {
      return;
    }

    effectiveChatIdRef.current = chatId;
    setIsConnecting(true);
    setError(null);

    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('🔌 Arbitration socket connected, socket.id:', socket.id);
      console.log('🔌 Joining chat room:', `chat_${chatId}`);
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);

      // Join the chat room
      socket.emit('join_chat', { chatId });
    });

    socket.on('joined_chat', (data: any) => {
      console.log('✅ Successfully joined chat room:', data);
      // Backend may resolve disputeId -> actual chatId
      if (data?.chatId) {
        effectiveChatIdRef.current = String(data.chatId);
        console.log('✅ Effective chatId set to:', effectiveChatIdRef.current);
      }
    });

    // Surface room-join rejections (e.g. access denied) instead of failing
    // silently — the socket stays connected but never joins the chat room, so
    // without this the admin sees a green "connected" badge yet no real-time
    // messages arrive.
    socket.on('join_chat_error', (data: any) => {
      console.error('❌ join_chat rejected by server:', data);
      setError(data?.error || 'عدم دسترسی به اتاق چت');
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Arbitration socket disconnected:', reason);
      setIsConnected(false);

      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        socket.connect();
      }
    });

    socket.on('connect_error', (err) => {
      console.error('🔌 Socket connection error:', err);
      setError('خطا در اتصال به سرور چت');
      setIsConnecting(false);
    });

    // Chat events
    socket.on('new_message', (data: any) => {
      console.log('📨 [useArbitrationSocket] New message received via socket:', data);
      console.log('📨 [useArbitrationSocket] Expected chatId:', chatId, 'effectiveChatId:', effectiveChatIdRef.current);
      console.log('📨 [useArbitrationSocket] Received chatId:', data?.chatId);

      // Backend sends { chatId, message } format
      const raw: any = data?.message || data;
      console.log('📨 [useArbitrationSocket] Extracted message (raw):', raw);

      // Normalize payload differences between arbitration module (id) and main chat ( _id )
      const normalized: any = raw
        ? {
            ...raw,
            id: raw.id || raw._id,
            // keep timestamp consistent for UI
            timestamp: raw.timestamp || raw.sentAt || raw.createdAt,
          }
        : null;

      console.log('📨 [useArbitrationSocket] Normalized message:', normalized);

      if (normalized && normalized.id) {
        console.log('📨 [useArbitrationSocket] Calling onNewMessage callback');
        onNewMessage?.(normalized as IChatMessage);
      } else {
        console.warn('⚠️ [useArbitrationSocket] Message missing id, not calling callback');
      }
    });

    // Typing (backend emits user_typing with { chatId, userId, isTyping })
    socket.on('user_typing', (data: any) => {
      const userId = data?.userId;
      const isTyping = data?.isTyping !== false;
      const userName = data?.userName || '';

      if (!userId) return;

      setTypingUsers((prev) => {
        const next = new Map(prev);
        if (isTyping) {
          next.set(userId, userName);
        } else {
          next.delete(userId);
        }
        return next;
      });

      // Auto-expire: clear stale indicator after 4s if no refresh/stop arrives
      const existingTimer = typingExpireTimersRef.current.get(userId);
      if (existingTimer) clearTimeout(existingTimer);
      if (isTyping) {
        typingExpireTimersRef.current.set(
          userId,
          setTimeout(() => {
            typingExpireTimersRef.current.delete(userId);
            setTypingUsers((prev) => {
              const next = new Map(prev);
              next.delete(userId);
              return next;
            });
          }, 4000)
        );
      } else {
        typingExpireTimersRef.current.delete(userId);
      }

      if (isTyping) onTyping?.({ userId, userName });
      else onStopTyping?.({ userId });
    });

    socket.on('user_joined', (data: { userId: string; userName: string }) => {
      console.log('👤 User joined chat:', data);
      onUserJoined?.(data);
    });

    socket.on('user_left', (data: { userId: string; userName: string }) => {
      console.log('👤 User left chat:', data);
      onUserLeft?.(data);
    });

    socket.on('chat_closed', () => {
      console.log('🔒 Chat closed');
      onChatClosed?.();
    });

    // Cleanup
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingExpireTimersRef.current.forEach((t) => clearTimeout(t));
      typingExpireTimersRef.current.clear();
      socket.emit('leave_chat', { chatId: effectiveChatIdRef.current || chatId });
      socket.disconnect();
      socketRef.current = null;
      effectiveChatIdRef.current = null;
      setIsConnected(false);
      setTypingUsers(new Map());
    };
  }, [chatId, enabled, onNewMessage, onTyping, onStopTyping, onUserJoined, onUserLeft, onChatClosed]);

  // Send typing indicator
  const sendTyping = useCallback(() => {
    if (!socketRef.current || !isConnected) return;

    const id = effectiveChatIdRef.current || chatId;
    if (!id) return;
    // Backend expects { chatId, isTyping }
    socketRef.current.emit('typing', { chatId: id, isTyping: true });

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Auto stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      sendStopTyping();
    }, 3000);
  }, [chatId, isConnected]);

  // Send stop typing indicator
  const sendStopTyping = useCallback(() => {
    if (!socketRef.current || !isConnected) return;

    const id = effectiveChatIdRef.current || chatId;
    if (!id) return;
    // Backend expects { chatId, isTyping }
    socketRef.current.emit('typing', { chatId: id, isTyping: false });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [chatId, isConnected]);

  // Manually join chat
  const joinChat = useCallback(() => {
    if (!socketRef.current || !chatId || !isConnected) return;
    effectiveChatIdRef.current = chatId;
    socketRef.current.emit('join_chat', { chatId });
  }, [chatId, isConnected]);

  // Manually leave chat
  const leaveChat = useCallback(() => {
    if (!socketRef.current || !isConnected) return;
    const id = effectiveChatIdRef.current || chatId;
    if (!id) return;
    socketRef.current.emit('leave_chat', { chatId: id });
  }, [chatId, isConnected]);

  return {
    isConnected,
    isConnecting,
    error,
    typingUsers,
    sendTyping,
    sendStopTyping,
    joinChat,
    leaveChat,
  };
};

export default useArbitrationSocket;
